import React, {
  useReducer,
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActiveFilterOption } from '../Atoms/ProductAtoms';
import { ProductSizeInterface } from '../../Interface/ProductSize';
import { ProductAttributeInterface } from '../../Interface/ProductAttribute';

export interface FilterState {
  brand_ids: string[];
  category_ids: string[];
  sub_category_ids: string[];
  sale_type: string[];
  sale_percent: string[];
  sortBy: string | null;
  page: string;
  view: 'table' | 'grid' | 'list';
  product_ids: string[];
  product_attribute_ids: string[];
  product_size_ids: string[];
  q: string;
  favs_only: string;
}

type ProductView = 'table' | 'grid' | 'list';

const PRODUCT_VIEW_SESSION_STORAGE_KEY = 'productsView';
const VALID_PRODUCT_VIEWS: ProductView[] = ['table', 'grid', 'list'];

const isValidProductView = (value: unknown): value is ProductView => {
  return (
    typeof value === 'string' &&
    VALID_PRODUCT_VIEWS.includes(value as ProductView)
  );
};

const getStoredProductView = (): ProductView | null => {
  if (typeof window === 'undefined') return null;

  try {
    const value = window.sessionStorage.getItem(
      PRODUCT_VIEW_SESSION_STORAGE_KEY
    );
    return isValidProductView(value) ? value : null;
  } catch {
    return null;
  }
};

const setStoredProductView = (value: ProductView): void => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(PRODUCT_VIEW_SESSION_STORAGE_KEY, value);
  } catch {
    // Ignore storage errors (private mode, disabled storage, etc.)
  }
};

export const DEFAULT_PRODUCTS_FILTER: FilterState = {
  brand_ids: [],
  category_ids: [],
  sub_category_ids: [],
  sale_type: [],
  sale_percent: [],
  sortBy: 'available',
  page: '1',
  view: 'grid',
  product_ids: [],
  product_size_ids: [],
  product_attribute_ids: [],
  q: '',
  favs_only: '',
};

export const PRODUCTS_FILTER: (keyof FilterState)[] = [
  'brand_ids',
  'category_ids',
  'sub_category_ids',
  'sale_type',
  'sale_percent',
  'sortBy',
  'page',
  'view',
  'product_ids',
  'product_size_ids',
  'product_attribute_ids',
  'q',
  'favs_only',
] as const;

export type ArrayFilterKeys = {
  [K in keyof FilterState]: FilterState[K] extends Array<any> ? K : never;
}[keyof FilterState];

export interface ActiveFilterMeta {
  value: string | number;
  label: string;
}

interface ProductsFilterContextT {
  filters: FilterState;
  toggleArrayFilter: (
    filterKey: ArrayFilterKeys,
    value: string | number,
    label?: string
  ) => void;
  setSingleFilter: (
    filterKey: keyof FilterState,
    value: string | number | null
  ) => void;
  setArrayFilter: (
    filterKey: ArrayFilterKeys,
    values: string[],
    labels?: ActiveFilterMeta[]
  ) => void;
  updateFilters: (
    updates: Partial<FilterState>,
    activeFilterUpdates?: Partial<Record<ArrayFilterKeys, ActiveFilterMeta[]>>
  ) => void;
  clearFilters: (extraKeys?: string[]) => void;
  registerActiveFilterLabels: (
    filterKey: ArrayFilterKeys,
    items: ActiveFilterMeta[]
  ) => void;
  registerData: (
    data: (ProductSizeInterface | ProductAttributeInterface)[]
  ) => void;
  activeFiltersWithLabels: Record<string, ActiveFilterOption[]>;
  queryFilterString: string;
  hasActiveFilters: boolean;
}

const ProductsFilterContext = createContext<ProductsFilterContextT | null>(
  null
);

const getInitialFilters = (searchParams: URLSearchParams): FilterState => {
  const {
    brand_ids,
    category_ids,
    sub_category_ids,
    sale_type,
    sale_percent,
    sortBy,
    page,
    view,
    product_ids,
    product_size_ids,
    product_attribute_ids,
    q,
    favs_only,
  } = DEFAULT_PRODUCTS_FILTER;

  const viewFromUrl = searchParams.get('view');
  const viewFromSession = getStoredProductView();

  return {
    q: searchParams.get('q') || q,
    product_ids:
      searchParams.get('product_ids')?.split(',').filter(Boolean) ||
      product_ids,
    product_size_ids:
      searchParams.get('product_size_ids')?.split(',').filter(Boolean) ||
      product_size_ids,
    product_attribute_ids:
      searchParams.get('product_attribute_ids')?.split(',').filter(Boolean) ||
      product_attribute_ids,
    favs_only: searchParams.get('favs_only') || favs_only,
    brand_ids:
      searchParams.get('brand_ids')?.split(',').filter(Boolean) || brand_ids,
    category_ids:
      searchParams.get('category_ids')?.split(',').filter(Boolean) ||
      category_ids,
    sub_category_ids:
      searchParams.get('sub_category_ids')?.split(',').filter(Boolean) ||
      sub_category_ids,
    sale_type:
      searchParams.get('sale_type')?.split(',').filter(Boolean) || sale_type,
    sale_percent:
      searchParams.get('sale_percent')?.split(',').filter(Boolean) ||
      sale_percent,
    sortBy: searchParams.get('sortBy') || sortBy,
    page: searchParams.get('page') || page,
    view: isValidProductView(viewFromUrl)
      ? viewFromUrl
      : viewFromSession || view,
  };
};

type FilterAction =
  | { type: 'UPDATE_MULTI_FILTER'; newFilterState: FilterState }
  | {
      type: 'UPDATE_SINGLE_FILTER';
      newFilterState: FilterState;
    }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'UPDATE_FILTERS_FROM_URL'; filters: FilterState };

function filtersReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'UPDATE_MULTI_FILTER': {
      const { newFilterState } = action;
      return newFilterState;
    }

    case 'UPDATE_SINGLE_FILTER': {
      const { newFilterState } = action;
      return newFilterState;
    }

    case 'CLEAR_FILTERS': {
      return DEFAULT_PRODUCTS_FILTER;
    }

    case 'UPDATE_FILTERS_FROM_URL': {
      return action.filters;
    }
    default:
      return state;
  }
}

export const ProductsFilterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, dispatch] = useReducer(
    filtersReducer,
    getInitialFilters(searchParams)
  );

  // Label Registry: ID -> Label string
  const [labels, setLabels] = useState<Record<string, string>>({});

  /**
   * Manually registers a set of labels for specific filter values.
   * Useful when labels are known upfront (e.g., from a hardcoded list or a specific API response).
   */
  const registerActiveFilterLabels = useCallback(
    (filterKey: ArrayFilterKeys, items: ActiveFilterMeta[]) => {
      setLabels((prev) => {
        let hasUpdate = false;
        const next = { ...prev };
        items.forEach(({ value, label }) => {
          const valStr = value.toString();
          //If the label is identical to the value (ID),
          // it's a placeholder. We ignore it to prevent the "ID Flicker" in the UI.
          if (label && valStr !== label.toString() && next[valStr] !== label) {
            next[valStr] = label;
            hasUpdate = true;
          }
        });
        return hasUpdate ? next : prev;
      });
    },
    []
  );

  /**
   * Automatically ingests product data to populate the label registry.
   * Traverses ProductSize and ProductAttribute structures to extract human-readable
   * names for products and sizes, ensuring the UI can resolve IDs to labels.
   */
  const registerData = useCallback(
    (data: (ProductSizeInterface | ProductAttributeInterface)[]) => {
      if (!data || data.length === 0) return;

      const newLabels: Record<string, string> = {};

      data.forEach((p: any) => {
        // Handle ProductSize structure
        if (p.unit_value !== undefined) {
          const id = (p.erp_id || p.id)?.toString();
          if (id) {
            newLabels[id] = `${p.name || p.product?.name || ''}`.trim();
          }
          if (p.product?.erp_id || p.product?.id) {
            newLabels[(p.product.erp_id || p.product.id).toString()] =
              p.product.name;
          }
        }
        // Handle ProductAttribute structure
        else if (p.product_size !== undefined) {
          const attrId = (p.erp_id || p.id)?.toString();
          if (attrId) newLabels[attrId] = p.name;

          const size = p.product_size;
          const sizeId = (size.erp_id || size.id)?.toString();
          if (sizeId) {
            newLabels[sizeId] = `${size?.name || ''}`.trim();
          }
          if (size.product?.erp_id || size.product?.id) {
            newLabels[(size.product.erp_id || size.product.id).toString()] =
              size.product.name;
          }
        }
      });

      if (Object.keys(newLabels).length > 0) {
        setLabels((prev) => {
          const next = { ...prev };
          let hasUpdate = false;
          Object.entries(newLabels).forEach(([id, label]) => {
            if (next[id] !== label) {
              next[id] = label;
              hasUpdate = true;
            }
          });
          return hasUpdate ? next : prev;
        });
      }
    },
    []
  );

  /**
   * Derived state: active filters with their resolved labels.
   */
  const activeFiltersWithLabels = useMemo(() => {
    const result: Record<string, { name: string; value: string }[]> = {};

    Object.entries(filters).forEach(([key, value]) => {
      const filterKey = key as keyof FilterState;
      if (Array.isArray(value) && value.length > 0) {
        // Only return labels for keys that should appear in the "Active Filters" list
        if (
          [
            'brand_ids',
            'category_ids',
            'sub_category_ids',
            'sale_type',
            'sale_percent',
            'product_ids',
            'product_size_ids',
            'product_attribute_ids',
          ].includes(filterKey)
        ) {
          const itemsWithLabels = value
            .map((id) => ({
              value: id,
              name: labels[id.toString()],
            }))
            .filter((item): item is { value: string; name: string } =>
              Boolean(item.name)
            );

          if (itemsWithLabels.length > 0) {
            result[filterKey] = itemsWithLabels;
          }
        }
      }
    });

    return result;
  }, [filters, labels]);

  /**
   * The primary method for updating multiple filters simultaneously.
   * Handles session storage for view modes, updates the internal state,
   * registers any provided labels, and synchronizes the change to the URL.
   */
  const updateFilters = useCallback(
    (
      updates: Partial<FilterState>,
      activeFilterUpdates?: Partial<Record<ArrayFilterKeys, ActiveFilterMeta[]>>
    ) => {
      if (updates.view && isValidProductView(updates.view)) {
        setStoredProductView(updates.view);
      }

      const newFilterState: FilterState = {
        ...filters,
        page: DEFAULT_PRODUCTS_FILTER.page,
        ...updates,
      };

      dispatch({ type: 'UPDATE_MULTI_FILTER', newFilterState });

      // If explicit labels provided during update, register them
      if (activeFilterUpdates) {
        const labelsToReg: Record<string, string> = {};
        Object.values(activeFilterUpdates).forEach((items) => {
          items?.forEach((item) => {
            labelsToReg[item.value.toString()] = item.label;
          });
        });
        if (Object.keys(labelsToReg).length > 0) {
          setLabels((prev) => ({ ...prev, ...labelsToReg }));
        }
      }

      // Sync all updates to URL
      setSearchParams(
        () => {
          const params = new URLSearchParams(window.location.search);

          Object.entries(updates).forEach(([key, value]) => {
            const filterKey = key as keyof FilterState;

            if (Array.isArray(value)) {
              if (value.length > 0) {
                params.set(filterKey, value.join(','));
              } else {
                params.delete(filterKey);
              }
            } else {
              if (value) {
                params.set(filterKey, value as string);
              } else {
                params.delete(filterKey);
              }
            }

            if (
              filterKey === 'page' &&
              value === DEFAULT_PRODUCTS_FILTER.page
            ) {
              params.delete('page');
            }
          });

          return params;
        },
        { replace: true }
      );
    },
    [filters, setSearchParams]
  );

  /**
   * Toggles a single value within an array-based filter (e.g., Brand or Category).
   * If a label is provided, it is automatically added to the registry.
   */
  const toggleArrayFilter = useCallback(
    (filterKey: ArrayFilterKeys, value: string | number, label?: string) => {
      const valueStr = value.toString();
      const currentArray = (filters[filterKey] as string[]) || [];

      const isSelected = currentArray.includes(valueStr);
      const updatedArray = isSelected
        ? currentArray.filter((item) => item !== valueStr)
        : [...currentArray, valueStr];

      if (label) {
        setLabels((prev) => ({ ...prev, [valueStr]: label }));
      }

      updateFilters({
        [filterKey]: updatedArray,
        page: DEFAULT_PRODUCTS_FILTER.page,
      });
    },
    [filters, updateFilters]
  );

  /**
   * Updates a single non-array filter value (e.g., Search query or Sort order).
   * Resets the page to 1 upon any change.
   */
  const setSingleFilter = useCallback(
    (filterKey: keyof FilterState, value: string | number | null) => {
      if (filterKey === 'page') {
        updateFilters({
          page: value?.toString() || DEFAULT_PRODUCTS_FILTER.page,
        });
      } else {
        updateFilters({
          [filterKey]: value?.toString() || null,
          page: DEFAULT_PRODUCTS_FILTER.page,
        });
      }
    },
    [updateFilters]
  );

  /**
   * Replaces the entire array for a specific filter key.
   * Optionally registers a list of labels for the new values.
   */
  const setArrayFilter = useCallback(
    (
      filterKey: ArrayFilterKeys,
      values: string[],
      labels?: ActiveFilterMeta[]
    ) => {
      if (labels && labels.length > 0) {
        const labelsToReg: Record<string, string> = {};
        labels.forEach((l) => (labelsToReg[l.value.toString()] = l.label));
        setLabels((prev) => ({ ...prev, ...labelsToReg }));
      }

      updateFilters({
        [filterKey]: values,
      });
    },
    [updateFilters]
  );

  /**
   * Resets all filters to their default values while optionally preserving
   * specific state like the current view or favorites.
   */
  const clearFilters = useCallback(
    (extraKeys?: string[]) => {
      const preservedView = filters.view;
      const preservedFavs = filters.favs_only;

      dispatch({
        type: 'UPDATE_MULTI_FILTER',
        newFilterState: {
          ...DEFAULT_PRODUCTS_FILTER,
          view: preservedView,
          favs_only: preservedFavs,
        },
      });

      const params = new URLSearchParams(window.location.search);

      PRODUCTS_FILTER.forEach((key) => {
        if (key === 'view' || key === 'favs_only') return;
        params.delete(key);
      });

      if (extraKeys) {
        extraKeys.forEach((key) => {
          if (key === 'view' || key === 'favs_only') return;
          params.delete(key);
        });
      }

      params.set('view', preservedView);
      if (preservedFavs) {
        params.set('favs_only', preservedFavs);
      } else {
        params.delete('favs_only');
      }

      setSearchParams(params, { replace: true });
    },
    [filters.view, filters.favs_only, setSearchParams]
  );

  // from URL to State sync
  useEffect(() => {
    const urlView = searchParams.get('view');
    if (isValidProductView(urlView)) {
      setStoredProductView(urlView);
    }

    const urlFilters = getInitialFilters(searchParams);
    const hasChanged = JSON.stringify(urlFilters) !== JSON.stringify(filters);

    if (hasChanged) {
      dispatch({ type: 'UPDATE_FILTERS_FROM_URL', filters: urlFilters });
    }

    setHasActiveFilters(
      PRODUCTS_FILTER.some(
        (key) =>
          key !== 'view' &&
          key !== 'page' &&
          key !== 'favs_only' &&
          searchParams.has(key)
      )
    );
  }, [searchParams]);

  const queryFilterString = useMemo(() => {
    const params = new URLSearchParams();

    PRODUCTS_FILTER.forEach((filterKey) => {
      // exceptions
      if (filterKey === 'page' || filterKey === 'view') return;
      if (
        filterKey === 'sortBy' &&
        filters[filterKey] === DEFAULT_PRODUCTS_FILTER.sortBy
      ) {
        params.set(filterKey, DEFAULT_PRODUCTS_FILTER.sortBy);
        return;
      }

      const value = searchParams.get(filterKey);
      if (value) params.set(filterKey, value);
    });

    return params.toString();
  }, [searchParams]);

  return (
    <ProductsFilterContext.Provider
      value={{
        filters,
        toggleArrayFilter,
        setArrayFilter,
        updateFilters,
        setSingleFilter,
        clearFilters,
        registerActiveFilterLabels,
        registerData,
        activeFiltersWithLabels,
        queryFilterString,
        hasActiveFilters,
      }}
    >
      {children}
    </ProductsFilterContext.Provider>
  );
};

export function useProductsFilter() {
  const context = useContext(ProductsFilterContext);
  if (!context) {
    throw new Error(
      'useProductsFilter must be used within ProductsFilterProvider'
    );
  }
  return context;
}

export default ProductsFilterProvider;
